import { t } from "i18next";
import { match, P } from "ts-pattern";
import { z } from "zod";

import {
  longTextSchema,
  nonFutureDateTimeSchema,
  shortTextSchema,
  yesNoSchema,
  yesOnlySchema,
} from "@thalamos/common";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import { tk } from "../../types/Translation.js";
import {
  getFirstPlaceOfSafetyArrivalDateTime,
  isPoliceLeftTimeOutOfExpectedRange,
} from "../../workflowLogic/calculateDetentionTime.js";
import { getSupportedIncidentTypeSelection } from "../../workflowLogic/helpers/getIncidentTypeSelection.js";
import { getMcaAccompaniment } from "../../workflowLogic/helpers/getMcaAccompaniment.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";
import { hasSharedForm } from "../../workflowLogic/helpers/hasSharedForm.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldConditionallyVisible({
    fieldName: "confirmEndForm",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).build(),
    label: tk("finishForm.confirmEndForm.title"),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => false)
        .with(undefined, () => false)
        .exhaustive() &&
      match(getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"))
        .with("no", () => true)
        .with("yes", () => false)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "whyNotSharing",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "nhsUsingOtherProcess" as const,
          "internetConnectivityIssue" as const,
          "emailNotReceived" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("finishForm.whyNotSharing.title"),
    requiredLabel: tk("finishForm.whyNotSharing.required"),
    options: [
      {
        label: tk("finishForm.whyNotSharing.options.nhsUsingOtherProcess"),
        value: "nhsUsingOtherProcess",
      },
      {
        label: tk("finishForm.whyNotSharing.options.internetConnection"),
        value: "internetConnectivityIssue",
      },
      {
        label: tk("finishForm.whyNotSharing.options.emailNotReceived"),
        value: "emailNotReceived",
      },
      {
        label: tk("finishForm.whyNotSharing.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => true)
        .with(undefined, () => true)
        .exhaustive() &&
      match(getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"))
        .with("no", () => false)
        .with("yes", () => true)
        .exhaustive() &&
      !hasSharedForm({ incidentWorkItem }),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyNotSharingOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) => formValues.whyNotSharing?.value === "other",
    label: tk("finishForm.whyNotSharingOther.title"),
    multiline: true,
    rows: "5",
    requiredLabel: tk("finishForm.whyNotSharingOther.required"),
  })
  .withFieldConditionallyVisible({
    fieldName: "staffNameLeftWith",
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match([
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
        getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"),
      ])
        .with(["mca", "no"], () => false)
        .with(["mca", "yes"], () => true)
        .with(["136", P._], () => false)
        .with([undefined, P._], () => true)
        .exhaustive(),
    label: tk("finishForm.staffNameLeftWith.title"),
    requiredLabel: tk("finishForm.staffNameLeftWith.required"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "informStaff",
    type: "banner",
    body: ({ incidentWorkItem }) => ({
      translationKey: tk("finishForm.informStaff"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    bannerType: "warning",
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      (match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("136", () => true)
        .with("mca", () => false)
        .with(undefined, () => false)
        .exhaustive() ||
        match(getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"))
          .with("no", () => false)
          .with("yes", () => true)
          .exhaustive()),
  })
  .withFieldConditionallyVisible({
    fieldName: "dateTimeLeft",
    type: "date-time",
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("finishForm.dateLeft"),
    timeLabel: tk("finishForm.timeLeft"),
    disableFuture: true,
    isVisible: ({ formValues }) => formValues.confirmEndForm?.value !== "yes",
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "incidentDuration",
    type: "incident-duration",
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("finishForm.totalPosTime"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    getDateTimeLeft: ({ formValues }) => formValues.dateTimeLeft?.value,
    getArrivalAtFirstPlaceOfSafetyDateTime: ({ incidentWorkItem }) =>
      getFirstPlaceOfSafetyArrivalDateTime(incidentWorkItem),
    isVisible: ({ formValues }) => formValues.confirmEndForm?.value !== "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmDateTimeLeaving",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("finishForm.confirmDateTimeLeaving"),
    requiredLabel: tk("finishForm.confirmDateTimeLeavingRequired"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      const dateTimeLeft = formValues.dateTimeLeft ?? undefined;
      if (dateTimeLeft === undefined) {
        return false;
      }

      return isPoliceLeftTimeOutOfExpectedRange(
        dateTimeLeft.value,
        incidentWorkItem,
      );
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "wasPersonAssessed",
    label: tk("finishForm.wasPersonAssessed"),
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
    isVisible: ({ formValues, incidentWorkItem }) =>
      formValues.confirmEndForm?.value !== "yes" &&
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("136", () => true)
        .with("mca", () => false)
        .with(undefined, () => false)
        .exhaustive(),
  })
  .withFieldConditionallyVisible({
    fieldName: "assessmentOutcomeKnown",
    label: tk("finishForm.assessmentOutcomeKnown"),
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
    isVisible: ({ formValues }) =>
      formValues.wasPersonAssessed?.value === "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "whatWasTheOutcome",
    type: "dropdown",
    label: tk("finishForm.whatWasTheOutcome.title"),
    ...new FieldBuilder()
      .withNullableSchema(
        z.enum(["discharged" as const, "admitted" as const, "other" as const]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk("finishForm.whatWasTheOutcome.options.discharged"),
        value: "discharged",
      },
      {
        label: tk("finishForm.whatWasTheOutcome.options.admitted"),
        value: "admitted",
      },
      {
        label: tk("finishForm.whatWasTheOutcome.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.assessmentOutcomeKnown?.value === "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "whatWasTheOutcomeOther",
    type: "textbox",
    label: tk("finishForm.whatWasTheOutcome.otherOutcome"),
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.whatWasTheOutcome?.value === "other",
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "typeOfFurtherCareReceived",
    type: "dropdown",
    label: tk("finishForm.typeOfFurtherCareReceived.title"),
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "furtherDetention" as const,
          "voluntary" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "finishForm.typeOfFurtherCareReceived.options.furtherDetention",
        ),
        value: "furtherDetention",
      },
      {
        label: tk("finishForm.typeOfFurtherCareReceived.options.voluntary"),
        value: "voluntary",
      },
      {
        label: tk("finishForm.typeOfFurtherCareReceived.options.unknown"),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.whatWasTheOutcome?.value === "admitted",
  })

  .withPage(["confirmEndForm"]) // MCA - without accompaniment]
  .withPage([
    "whyNotSharing", // MCA - with accompaniment, without sharing
    "whyNotSharingOther", // MCA - with accompaniment, without sharing
  ])
  .withPage([
    "staffNameLeftWith", // MCA - with accompaniment, with sharing
  ])
  .withPage([
    "informStaff",
    "dateTimeLeft",
    "incidentDuration",
    "confirmDateTimeLeaving",
  ])
  .withPage(["wasPersonAssessed"])
  .withPage(["assessmentOutcomeKnown"])
  .withPage(["whatWasTheOutcome", "whatWasTheOutcomeOther"])
  .withPage(["typeOfFurtherCareReceived"])
  .build({ t });

export type FinishFormData = ReturnType<typeof formSpecification>["formType"];
